const DELIMITER_CANDIDATES = [",", ";"] as const;

function stripUtf8Bom(bytes: Uint8Array): Uint8Array {
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return bytes.subarray(3);
  }
  return bytes;
}

function stripUtf8BomFromDecodedText(text: string): string {
  return text.startsWith("\uFEFF") ? text.slice(1) : text;
}

export function detectEncodingLabel(bytes: Uint8Array): "utf-8" | "latin1" {
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return "utf-8";
  }
  const hasHigh = bytes.some((b) => b >= 0x80);
  if (!hasHigh) {
    return "utf-8";
  }
  const asUtf8 = new TextDecoder("utf-8", { fatal: false }).decode(bytes);
  if (asUtf8.includes("\uFFFD")) {
    return "latin1";
  }
  return "utf-8";
}

function bytesToText(bytes: Uint8Array, enc: "utf-8" | "latin1"): string {
  if (enc === "utf-8") {
    return new TextDecoder("utf-8").decode(stripUtf8Bom(bytes));
  }
  return new TextDecoder("iso-8859-1").decode(bytes);
}

function countDelimiterOutsideQuotes(line: string, delim: string): number {
  let count = 0;
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (!inQuotes && c === delim) {
      count += 1;
    }
  }
  return count;
}

function splitCsvTextLines(text: string): string[] {
  const lines: string[] = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      current += char;
      if (inQuotes && text[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes) {
      const breakLength = isUnquotedRecordBreak(text, i);
      if (breakLength > 0) {
        lines.push(current);
        current = "";
        i += breakLength - 1;
        continue;
      }
    }
    current += char;
  }
  lines.push(current);
  return lines;
}

function isUnquotedRecordBreak(text: string, index: number): number {
  const c = text[index];
  if (c === "\n") {
    return 1;
  }
  if (c === "\r") {
    return text[index + 1] === "\n" ? 2 : 1;
  }
  return 0;
}

export function detectDelimiter(text: string): string {
  const lines = splitCsvTextLines(text)
    .map((l) => l.trim())
    .filter((l) => l.length > 0)
    .slice(0, 24);
  if (lines.length === 0) {
    return ",";
  }
  let best = ",";
  let bestScore = -1;
  for (const d of DELIMITER_CANDIDATES) {
    const colCounts = lines.map((line) => countDelimiterOutsideQuotes(line, d) + 1);
    const firstCount = colCounts[0];
    if (firstCount < 2) {
      continue;
    }
    const allSame = colCounts.every((c) => c === firstCount);
    const score = allSame ? firstCount * 1000 + lines.length * 10 : colCounts.filter((c) => c === firstCount).length * 100;
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  return best;
}

export function splitCsvLine(line: string, delimiter: string): string[] {
  const parts: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (!inQuotes && c === delimiter) {
      parts.push(cur.trim());
      cur = "";
    } else if (!inQuotes && isUnquotedRecordBreak(line, i) > 0) {
      break;
    } else {
      cur += c;
    }
  }
  parts.push(cur.trim());
  return parts.map((p) => p.replace(/^"|"$/g, "").trim());
}

export function analyzeCsvFile(
  file: File
): Promise<{ headers: string[]; encoding: string; delimiter: string }> {
  return file.arrayBuffer().then((buf) => {
    const bytes = new Uint8Array(buf);
    if (bytes.length === 0) {
      return Promise.reject(new Error("File is empty"));
    }
    const encLabel = detectEncodingLabel(bytes);
    const text = bytesToText(bytes, encLabel);
    const delimiter = detectDelimiter(text);
    const lines = splitCsvTextLines(text).filter((l) => l.length > 0);
    if (lines.length === 0) {
      return Promise.reject(new Error("File is empty"));
    }
    const headers = splitCsvLine(text, delimiter);
    const encoding = encLabel === "latin1" ? "latin1" : "utf-8";
    return { headers, encoding, delimiter };
  });
}

export const parseCSVHeaders = (file: File, encoding: string, delimiter: string = ","): Promise<string[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const raw = e.target?.result as string;
      if (!raw) {
        reject(new Error("Failed to read file"));
        return;
      }
      const text = stripUtf8BomFromDecodedText(raw);
      const lines = splitCsvTextLines(text).filter((l) => l.length > 0);
      if (lines.length === 0) {
        reject(new Error("File is empty"));
        return;
      }
      const headers = splitCsvLine(text, delimiter);
      resolve(headers);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file, encoding);
  });
};
